import { map, filter } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
import parseQueryParams from "../../utils/query";
// ----------------------------------------------------------------------

const EVENT_COLORS = {
  delivered: {
    backgroundColor: 'rgba(84, 214, 44, .24)',
    textColor: '#229A16'
  },
  success: {
    backgroundColor: 'rgba(24, 144, 255, .24)',
    textColor: '#0C53B7'
  },
  warning: {
    backgroundColor: 'rgb(255, 247, 104, .24)',
    textColor: 'rgb(255, 247, 104)'
  },
  error: {
    backgroundColor: 'rgba(255, 72, 66, .24)',
    textColor: '#B72136'
  },
}

const initialState = {
  isLoading: false,
  error: false,
  events: [],
  isOpenModal: false,
  selectedEventId: null,
  selectedRange: null
};

const slice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET EVENTS
    getEventsSuccess(state, action) {
      state.isLoading = false;

      const deliveredEvents = {
        events: [],
        ...EVENT_COLORS.delivered,
      };
      const successEvents = {
        events: [],
        ...EVENT_COLORS.success,
      };
      const warningEvents = {
        events: [],
        ...EVENT_COLORS.warning,
      };
      const overDueEvents = {
        events: [],
        ...EVENT_COLORS.error,
      };

      const events = {
        delivered: deliveredEvents,
        success: successEvents,
        warning: warningEvents,
        error: overDueEvents,
      }

      action.payload.forEach(ev => {
        const eventData = {
          id: ev.id,
          uuid: ev.uuid,
          title: `${ev.client?.name || 'Publico General'} - ${ev.uuid.split('-')[0]}`,
          date: ev.delivery_date,
          allDay: true,
          timezone: 'America/Mexico_City'
        };

        events[ev.stale_status].events.push(eventData);
      });
      state.events = Object.values(events);
    },

    // CREATE EVENT
    createEventSuccess(state, action) {
      const newEvent = action.payload;
      state.isLoading = false;
      state.events = [...state.events, newEvent];
    },

    // UPDATE EVENT
    updateEventSuccess(state, action) {
      const event = action.payload;
      const updateEvent = map(state.events, (_event) => {
        if (_event.id === event.id) {
          return event;
        }
        return _event;
      });

      state.isLoading = false;
      state.events = updateEvent;
    },

    // DELETE EVENT
    deleteEventSuccess(state, action) {
      const { eventId } = action.payload;
      const deleteEvent = filter(state.events, (user) => user.id !== eventId);
      state.isLoading = false;
      state.events = deleteEvent;
    },

    // SELECT EVENT
    selectEvent(state, action) {
      const eventId = action.payload;
      state.isOpenModal = true;
      state.selectedEventId = eventId;
    },

    // SELECT RANGE
    selectRange(state, action) {
      const { start, end } = action.payload;
      state.isOpenModal = true;
      state.selectedRange = { start, end };
    },

    // OPEN MODAL
    openModal(state) {
      state.isOpenModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
      state.selectedEventId = null;
      state.selectedRange = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { openModal, closeModal, selectEvent } = slice.actions;

// ----------------------------------------------------------------------

export function getEvents(year) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const { selectedCompany } = getState().company || {};
      const query = {
        fields: ['id', 'uuid', 'client', 'delivery_date', 'stale_status'],
        expand: ['client'],
        delivery_year: year,
        ...(selectedCompany?.id ? { company: selectedCompany.id } : {}),
      };
      const params = parseQueryParams(query);
      const response = await axios.get(`/ecommerce/sales/calendar/?${params}`);
      dispatch(slice.actions.getEventsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function createEvent(newEvent) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/api/calendar/events/new', newEvent);
      dispatch(slice.actions.createEventSuccess(response.data.event));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updateEvent(eventId, updateEvent) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/api/calendar/events/update', {
        eventId,
        updateEvent
      });
      dispatch(slice.actions.updateEventSuccess(response.data.event));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function deleteEvent(eventId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.post('/api/calendar/events/delete', { eventId });
      dispatch(slice.actions.deleteEventSuccess({ eventId }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function selectRange(start, end) {
  return async (dispatch) => {
    dispatch(
      slice.actions.selectRange({
        start: start.getTime(),
        end: end.getTime()
      })
    );
  };
}
